import type { RouteMeta, RouteRecordRaw } from 'vue-router'
import ListProducers from '@/views/producers/list-producers.vue'
import ManageProducer from '@/views/producers/manage-producers.vue'
import ListEvents from '@/views/events/list-events.vue'
import EditEvent from '@/views/events/edit-event.vue'
import CreateEvent from '@/views/events/create-event.vue'
import EventOverview from '@/views/events/overview/overview-index.vue'
import AccessControl from '@/views/events/access-control/access-control-index.vue'
import EventSeats from '@/views/events/seats/event-seats.vue'
import MockedEventAccess from '@/views/reports/mocked-event-access/mocked-event-access.vue'
import ListAffiliates from '@/views/affiliates/list-affiliates.vue'
import CreateAffiliates from '@/views/affiliates/create-affiliate.vue'
import EditAffiliates from '@/views/affiliates/edit-affiliate.vue'
import AccreditAffiliates from '@/views/affiliates/accredit-affiliate.vue'
import AffiliatesSales from '@/views/affiliates/sales-affiliate.vue'
import ListAdmins from '@/views/admins/list-admins.vue'
import EditAdmins from '@/views/admins/edit-admins.vue'
import ShowAdmins from '@/views/admins/show-admins.vue'
import CreateAdmins from '@/views/admins/create-admins.vue'
import ListCoupons from '@/views/coupons/list-coupons.vue'
import ReadCoupon from '@/views/coupons/read-coupon.vue'
import EditCoupon from '@/views/coupons/edit-coupon.vue'
import CreateCoupons from '@/views/coupons/create-coupon.vue'
import CreateProducer from '@/views/producers/create-producer.vue'
import EditProducer from '@/views/producers/edit-producer.vue'
import ReadProducer from '@/views/producers/read-producer.vue'
import ListPrePrint from '@/views/preprint/list-pre-print.vue'
import CreatePrePrint from '@/views/preprint/create-pre-print.vue'
import ListComplimentaryTickets from '@/views/complimentary-tickets/list-complimentary-tickets.vue'
import CreateComplimentaryTicket from '@/views/complimentary-tickets/create-complimentary-ticket.vue'
import ReadAffiliate from '@/views/affiliates/read-affiliate.vue'
import CreateEventConfiguration from '@/views/event-configurations/create-event-configuration.vue'
import ListEventConfiguration from '@/views/event-configurations/list-event-configuration.vue'
import EditEventConfiguration from '@/views/event-configurations/edit-event-configuration.vue'
import ListLocations from '@/views/locations/list-locations.vue'
import CreateLocations from '@/views/locations/create-locations.vue'
import EditLocations from '@/views/locations/edit-locations.vue'
import ListMappings from '@/views/mappings/list-mappings.vue'
import CreateMappings from '@/views/mappings/create-mappings.vue'
import EditMappings from '@/views/mappings/edit-mappings.vue'
import ListBanners from '@/views/banners/list-banners.vue'
import CreateBanner from '@/views/banners/create-banner.vue'
import EditBanner from '@/views/banners/edit-banner.vue'
import SortBanners from '@/views/banners/sort-banners.vue'
import ListUsers from '@/views/users/list-users.vue'
import EditUsers from '@/views/users/edit-users.vue'
import ListBiometricProviders from '@/views/biometry/providers/list-biometric-providers.vue'
import CreateBiometricProvider from '@/views/biometry/providers/create-biometric-provider.vue'
import EditBiometricProvider from '@/views/biometry/providers/edit-biometric-provider.vue'
import ShowBiometricProvider from '@/views/biometry/providers/show-biometric-provider.vue'
import ListRoles from '@/views/roles/list-roles.vue'
import EditRoles from '@/views/roles/edit-roles.vue'
import CreateRoles from '@/views/roles/create-roles.vue'

import { routes as pos } from '@/router/point-of-sales'
import { routes as reports } from '@/router/reports'
import { auth } from './middlewares/auth'
import ManageScheduledBatches from '@/views/batches/manage-scheduled-batches.vue'
import ListActivities from '@/views/activities/list-activities.vue'
import { producer } from './middlewares/producer'

// TS complains if you set the meta attribute without also providing middleware
type RouteRecord = Omit<RouteRecordRaw, 'meta'> & { meta?: Partial<RouteMeta> }

const routes: readonly RouteRecord[] = [
  {
    path: '/producers',
    name: 'list-producers',
    component: ListProducers,
    meta: { needsProducer: false },
  },
  {
    path: '/producers/manage',
    name: 'manage-producer',
    component: ManageProducer,
    meta: { needsProducer: false },
  },
  {
    path: '/producers/create',
    name: 'create-producer',
    component: CreateProducer,
    meta: { needsProducer: false },
  },
  {
    path: '/producers/:id/edit',
    name: 'edit-producer',
    component: EditProducer,
    meta: { needsProducer: false },
  },
  {
    path: '/producers/:id/read',
    name: 'read-producer',
    component: ReadProducer,
    meta: { needsProducer: false },
  },
  {
    path: '/events',
    name: 'list-events',
    component: ListEvents,
  },
  {
    path: '/events/create',
    name: 'create-event',
    component: CreateEvent,
  },
  {
    path: '/events/:id/edit',
    name: 'edit-event',
    component: EditEvent,
  },
  {
    path: '/events/:event?/overview',
    name: 'overview-events',
    component: EventOverview,
  },
  {
    path: '/events/:event?/access-control',
    name: 'access-control-events',
    component: AccessControl,
  },
  {
    path: '/events/:event/seats',
    name: 'event-seats',
    component: EventSeats,
  },
  {
    path: '/events/mocked-overview',
    name: 'mocked-overview-events',
    component: MockedEventAccess,
  },
  {
    path: '/pre-print',
    name: 'list-pre-print',
    component: ListPrePrint,
  },
  {
    path: '/pre-print/create',
    name: 'create-pre-print',
    component: CreatePrePrint,
  },
  {
    path: '/coupons',
    name: 'list-coupons',
    component: ListCoupons,
  },
  {
    path: '/complimentary-tickets',
    name: 'list-complimentary-tickets',
    component: ListComplimentaryTickets,
  },
  {
    path: '/complimentary-tickets/create',
    name: 'create-complimentary-ticket',
    component: CreateComplimentaryTicket,
  },
  {
    path: '/coupons/:id/read',
    name: 'read-coupon',
    component: ReadCoupon,
  },
  {
    path: '/coupons/:id/edit',
    name: 'edit-coupon',
    component: EditCoupon,
  },
  {
    path: '/coupons/create',
    name: 'create-coupons',
    component: CreateCoupons,
  },
  {
    path: '/batches/manage',
    name: 'manage-batches',
    component: ManageScheduledBatches,
  },
  {
    path: '/affiliates',
    name: 'list-affiliates',
    component: ListAffiliates,
  },
  {
    path: '/affiliates/create',
    name: 'create-affiliates',
    component: CreateAffiliates,
  },
  {
    path: '/affiliates/:id/edit',
    name: 'edit-affiliates',
    component: EditAffiliates,
  },
  {
    path: '/affiliates/events',
    name: 'accredit-affiliates',
    component: AccreditAffiliates,
  },
  {
    path: '/affiliates/sales',
    name: 'affiliates-sales',
    component: AffiliatesSales,
  },
  {
    path: '/affiliates/:id/read',
    name: 'read-affiliate',
    component: ReadAffiliate,
  },
  {
    path: '/admins',
    name: 'list-admins',
    component: ListAdmins,
    meta: { needsProducer: false },
  },
  {
    path: '/admins/:id/edit',
    name: 'edit-admins',
    component: EditAdmins,
    meta: { needsProducer: false },
  },
  {
    path: '/admins/:id/show',
    name: 'show-admins',
    component: ShowAdmins,
    meta: { needsProducer: false },
  },
  {
    path: '/admins/create',
    name: 'create-admins',
    component: CreateAdmins,
    meta: { needsProducer: false },
  },
  {
    path: '/roles',
    name: 'list-roles',
    component: ListRoles,
    meta: { needsProducer: false },
  },
  {
    path: '/roles/create',
    name: 'create-roles',
    component: CreateRoles,
    meta: { needsProducer: false },
  },
  {
    path: '/roles/:id/edit',
    name: 'edit-roles',
    component: EditRoles,
    meta: { needsProducer: false },
  },
  {
    path: '/event-configurations',
    name: 'list-event-configurations',
    component: ListEventConfiguration,
  },
  {
    path: '/event-configurations/:id/edit',
    name: 'edit-event-configuration',
    component: EditEventConfiguration,
  },
  {
    path: '/event-configurations/create',
    name: 'create-event-configuration',
    component: CreateEventConfiguration,
  },
  {
    path: '/banners/list',
    name: 'list-banners',
    component: ListBanners,
    meta: { needsProducer: false },
  },
  {
    path: '/banners/create',
    name: 'create-banner',
    component: CreateBanner,
    meta: { needsProducer: false },
  },
  {
    path: '/banners/:id/edit',
    name: 'edit-banner',
    component: EditBanner,
    meta: { needsProducer: false },
  },
  {
    path: '/banners/sort',
    name: 'sort-banners',
    component: SortBanners,
    meta: { needsProducer: false },
  },
  {
    path: '/locations',
    name: 'list-locations',
    component: ListLocations,
  },
  {
    path: '/locations/create',
    name: 'create-locations',
    component: CreateLocations,
  },
  {
    path: '/locations/:id/edit',
    name: 'edit-locations',
    component: EditLocations,
  },
  {
    path: '/mappings',
    name: 'list-mappings',
    component: ListMappings,
  },
  {
    path: '/mappings/create',
    name: 'create-mappings',
    component: CreateMappings,
  },
  {
    path: '/mappings/:id/edit',
    name: 'edit-mappings',
    component: EditMappings,
  },
  {
    path: '/users',
    name: 'list-users',
    component: ListUsers,
    meta: { needsProducer: false },
  },
  {
    path: '/users/:id/edit',
    name: 'edit-users',
    component: EditUsers,
    meta: { needsProducer: false },
  },
  {
    path: '/activities',
    name: 'list-activities',
    component: ListActivities,
  },
  {
    path: '/biometric-providers',
    name: 'list-biometric-providers',
    component: ListBiometricProviders,
  },
  {
    path: '/biometric-providers/create',
    name: 'create-biometric-providers',
    component: CreateBiometricProvider,
  },
  {
    path: '/biometric-providers/:id/edit',
    name: 'edit-biometric-providers',
    component: EditBiometricProvider,
  },
  {
    path: '/biometric-providers/:id/show',
    name: 'show-biometric-providers',
    component: ShowBiometricProvider,
  },
  ...reports,
  ...pos,
]

const routeMetaDefaults: RouteMeta = {
  needsProducer: true,
  middlewares: [auth, producer],
}

export default routes.map(
  route => ({
    ...route,
    meta: {
      ...routeMetaDefaults,
      ...(route.meta ?? {}),
    },
  }) as RouteRecordRaw,
)
